import * as React from 'react';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// MUI Components
import { useTheme } from '@mui/material/styles';
import { Box, Paper, IconButton, TextField, Chip, Checkbox } from '@mui/material';
import { Table, TableHead, TableBody, TableCell, TableContainer, TableFooter, TablePagination, TableRow } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';

// MUI Icons
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import DetailsIcon from '@mui/icons-material/Details';
import ClearIcon from '@mui/icons-material/Clear';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

// API Queries
import { useGetFilteredAssetRequestsQuery, useLazyGetFilteredAssetRequestsQuery } from '../../../features/assetrequest/assetReqApiSlice';
import { useGetStoreOfficesQuery } from '../../../features/storeoffices/storeofficesApiSlice';
import { useGetofficesQuery } from '../../../features/offices/officesApiSlice';

// Styles
import { TableContStyle, TableHeadStyle } from '../../../Styles/TableStyles';
import styled from 'styled-components';

// Utilities
import ExcelDownload from '../../../data/exceldownload';
import dayjs from 'dayjs';
import moment from 'moment';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const Button2Div = styled.div`
  display: flex;
  justify-content: end;
`;

const Wrapper = styled.div`
  display: flex;
  background-color: white;
  align-items: center;
`;

const styles = {
  tableContainer: {
    backgroundColor: '#f9f9f9',
  },
  tableHead: {
    backgroundColor: '#1976d2',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  cell: {
    fontSize: '0.875rem',
    textAlign: 'center',
  },
  actionButton: {
    textTransform: 'none',
    backgroundColor: '#1976d2',
    color: '#fff',
    padding: '5px 10px',
  },
};

const StyledButton = styled.button`
  width: 100px;
  height: 40px;
  padding: 5px 7px;
  border: none;
  border-radius:10px ;
  cursor: pointer;
  background-color: teal;
`

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const tableFontSize = 15;

// Debounce function
function debounce(func, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(this, args), delay);
  };
}

export default function UnivReqComp({
  keyArrayDwld,
  columnFilter,
  selectedOptions,
  setSelectedOptions,
  handleEditclick,
  filterProps,
  viewType = 'inbox'
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [assetdetailmodalopen, setAssetDetailmodalopen] = useState(false);
  const [assetdetailModaldata, setAssetDetailModaldata] = useState({});
  const [downloadData, setDownloadData] = useState({});
  const [tableData, setTableData] = useState([]);

  // Define filter states for asset requests
  const [filters, setFilters] = useState({
    uniqueReqId: "",
    req_type: [],
    req_creator_id: [],
    req_creator_details: "",
    bo_partial_issue: null,
    store_partial_issue:null,
    req_store: [],
    curr_status:[],
    from_created_at: "",
    to_created_at: "",
    curr_sent_to_string: "",
    curr_sent_to_tag:[],
    perm_curr_sent_to: [],
    perm_flow_users: [],
    perm_req_type: [],
    perm_creator_id: [],
    perm_req_store: [],
    perm_curr_status: [],
    perm_curr_sent_to_tag:[],
    req_data_sent_to_tags:[]
  }); 

  // Regular paginated query for displaying data
  const { data: filteredData, isLoading } = useGetFilteredAssetRequestsQuery({
    page: page + 1,
    limit: rowsPerPage,
    filters: filters,
  });

  // Lazy query to fetch data only when triggered
  const [fetchAllData, { data: allData, isLoading: allDataLoading }] = useLazyGetFilteredAssetRequestsQuery();

  // Get offices and stores for dropdown selections
  const { data: allstoreoffices } = useGetStoreOfficesQuery('allstorelists');
  const { data: allofficedata } = useGetofficesQuery('officelist');
  console.log(allData)
  // Extract store and office options
  const storeOptions = allstoreoffices ? 
    Object.values(allstoreoffices.entities).map(x => x.storeoffice) : []; 

  // Status options
  const statusOptions = ["Pending", "Approved", "Rejected", "Completed", "Closed"];

  // Request type options
  const reqTypeOptions = ["internal", "external"];

  // Fetch all data when filters change
  useEffect(() => {
    if (filters) {
      fetchAllData({
        page: 1,
        limit: 99999,
        filters: filters,
      });
    }
  }, [filters, fetchAllData]);


  useEffect(() => {
    if (filterProps) {
      // Create a new filters object that keeps existing filter values
      setFilters(prevFilters => {
        // These are the properties we want to reset
        const resetProps = {
          perm_curr_sent_to: [],
          perm_flow_users: [],
          perm_uniqueReqId: [],
          perm_req_type: [],
          perm_creator_id: [],
          perm_req_store: [],
          perm_curr_status: [],
          perm_curr_sent_to_tag: [],
          req_data_sent_to_tags: []
        };
        
        // Start with previous filters but reset the specific properties
        const newFilters = { ...prevFilters, ...resetProps };
        
        // Apply the new filterProps values
        for (let key in filterProps) {
          if (filterProps[key] !== undefined) {
            newFilters[key] = filterProps[key];
          }
        }
        
        return newFilters;
      });
    }
  }, [filterProps]);

  // Update table data when API data changes
  useEffect(() => {
    if (filteredData) {
      setTableData(filteredData.requests);
      
      // Debug logging for UnitHead inbox issue
      console.log('UnivReqComp Data Debug:', {
        totalRequests: filteredData.requests?.length || 0,
        filters: filters,
        sampleRequests: filteredData.requests?.slice(0, 3).map(req => ({
          id: req._id,
          uniqueReqId: req.uniqueReqId,
          curr_sent_to: req.curr_sent_to,
          curr_sent_to_tag: req.curr_sent_to_tag,
          curr_status: req.curr_status
        })) || []
      });
    }
  }, [filteredData]);

  // Prepare download data when API data is available
  useEffect(() => {
    if (allData?.requests && keyArrayDwld) {
      const tempDownloadData = structuredClone(allData.requests || []);
      
      // Format data for download
      const finalDownloadData = tempDownloadData.map(({ _id, req_data, ...others }) => ({
        ...others,
        created_by: req_data && req_data.length > 0 ? 
          `${req_data[0].sent_by?.name?.firstName || ''} ${req_data[0].sent_by?.name?.middleName || ''} ${req_data[0].sent_by?.name?.lastName || ''}`.trim() : '',
        created_date: moment(others.createdAt).format("DD/MM/YYYY HH:mm"),
        last_update: moment(others.updatedAt).format("DD/MM/YYYY HH:mm"),
        current_step: req_data ? req_data.length : 0,
      }));

      const finalSortedDownloadData = finalDownloadData.map((value) =>
        keyArrayDwld.reduce((acc, key) => ({ ...acc, [key]: value[key] }), {})
      );

      setDownloadData(finalSortedDownloadData);
    }
  }, [allData, keyArrayDwld]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };

  const handleAssetDetailModalClose = () => {
    setAssetDetailmodalopen(false);
  };

  const handleDetailsClick = (e, row) => {
    setAssetDetailModaldata(row);
    setAssetDetailmodalopen(true);
  };

  const handleSearchChange = debounce((event, newValue, name) => {
    let value;

    if (name) {
      // If a date is selected, check if it's valid and convert to ISO format
      if (newValue?.$d) {
        value = newValue?.toDate().toISOString(); // Convert to ISO format
      } else {
        value = newValue; // Reset if invalid date
      }
    } else {
      // Handle non-date input fields
      name = event.target.name;
      value = event.target.value;
      
      // Special handling for uniqueReqId to ensure it's always an array
      if (name === 'uniqueReqId' && value !== '') {
        value = [value]; // Convert to array
      }
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  }, 500);

  return (
    <>
      {keyArrayDwld && (
        <Button2Div>
          <ExcelDownload data={downloadData} filename={"assetDetail"} sheetName={"sheet1"} />
        </Button2Div>
      )}
      
      <div>
        <TableContainer component={Paper} sx={{ backgroundColor: TableContStyle.backgroundColor }}>
          <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
            <TableHead style={{ backgroundColor: TableHeadStyle.backgroundColor }}>
              <TableRow>
                <TableCell style={TableHeadStyle} sx={{ width: { md: "3%" } }}>
                  Sl.No.
                </TableCell>
                <TableCell style={TableHeadStyle} sx={{ width: { md: "13%" } }} align="left">
                  Req_ID
                </TableCell>
                {columnFilter?.includes("Req_Type") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "15%" } }} align="left">
                    Req_Type
                  </TableCell>
                )}
                {columnFilter?.includes("Req_Creator") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "11%" } }} align="left">
                    Req_Creator
                  </TableCell>
                )}
                {columnFilter?.includes("Store") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "11%" } }} align="left">
                    Store
                  </TableCell>
                )}

                {columnFilter?.includes("Created_At") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "11%" } }} align="left">
                    Created_At
                  </TableCell>
                )}
                {columnFilter?.includes("Currently_With") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "13%" } }} align="left">
                    Currently With
                  </TableCell>
                )}
                {columnFilter?.includes("Curr_Status") && (
                  <TableCell style={TableHeadStyle} sx={{ width: { md: "12%" } }} align="left">
                    Status
                  </TableCell>
                )}
                <TableCell style={TableHeadStyle} sx={{ width: { md: "12%" } }} align="left">
                  Action
                </TableCell>   
              </TableRow>

              <TableRow>
                <TableCell></TableCell>
                <TableCell>
                  <TextField
                    sx={{ background: "white" }}
                    name="uniqueReqId"
                    onChange={handleSearchChange}
                    label="Search Req_ID"
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                {columnFilter?.includes("Req_Type") && (
                  <TableCell>
                    <TextField
                      sx={{ background: "white" }}
                      name="req_type"
                      onChange={handleSearchChange}
                      label="Search Req Type"
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                )}
                {columnFilter?.includes("Req_Creator") && (
                  <TableCell>
                    <TextField
                      sx={{ background: "white" }}
                      name="req_creator_details"
                      onChange={handleSearchChange}
                      label="Req Creator"
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                )}
                
                {columnFilter?.includes("Store") && (
                  <TableCell>
                    <Autocomplete
                      multiple
                      id="checkboxes-tags-demo"
                      onChange={(event, newValue) => handleSearchChange(event, newValue, "req_store")}
                      size="small"
                      value={filters?.req_store}
                      options={storeOptions}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ fontSize: "2 rem" }}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 0, marginLeft: 0 }}
                            checked={selected}
                          />
                          {option}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Search Store" sx={{ background: "white", width: "100%" }} />
                      )}
                    />
                  </TableCell>
                )}

                {columnFilter?.includes("Created_At") && (
                  <TableCell>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={["DatePicker", "DatePicker"]} sx={{ margin: "0px", display: "flex" }}>
                        <Wrapper>
                          <DatePicker
                            label="From"
                            maxDate={filters?.to_created_at ? dayjs(filters.to_created_at) : null}
                            slotProps={{ textField: { size: "small" } }}
                            sx={{ backgroundColor: "white" }}
                            value={filters?.from_created_at ? dayjs(filters.created_at) : null}
                            format="DD/MM/YYYY"
                            onChange={(newValue) => handleSearchChange(null, newValue, "from_created_at")}
                          />
                          {filters?.from_created_at && (
                            <ClearIcon onClick={() => handleSearchChange(null, null, "from_created_at")} />
                          )}
                        </Wrapper>
                      </DemoContainer>
                    </LocalizationProvider>
                    {filters?.from_created_at && (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer components={["DatePicker", "DatePicker"]}>
                          <Wrapper>
                            <DatePicker
                              label="To"
                              minDate={filters?.from_created_at ? dayjs(filters.from_created_at) : null}
                              slotProps={{ textField: { size: "small" } }}
                              sx={{ backgroundColor: "white" }}
                              value={filters?.to_created_at ? dayjs(filters.to_created_at) : null}
                              format="DD/MM/YYYY"
                              onChange={(newValue) => handleSearchChange(null, newValue, "to_created_at")}
                            />
                            {filters?.to_created_at && (
                              <ClearIcon onClick={() => handleSearchChange(null, null, "to_created_at")} />
                            )}
                          </Wrapper>
                        </DemoContainer>
                      </LocalizationProvider>
                    )}
                  </TableCell>
                )}
                {columnFilter?.includes("Currently_With") && (
                  <TableCell>
                    <TextField
                      sx={{ background: "white" }}
                      name="curr_sent_to_string"
                      onChange={handleSearchChange}
                      label="Currently With"
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                )}                

                {columnFilter?.includes("Curr_Status") && (
                <TableCell>
                  <Autocomplete
                    multiple
                    id="checkboxes-tags-demo"
                    onChange={(event, newValue) => handleSearchChange(event, newValue, "curr_status")}
                    size="small"
                    value={filters?.curr_status}
                    options={statusOptions}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} style={{ fontSize: "2 rem" }}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 0, marginLeft: 0 }}
                          checked={selected}
                        />
                        {option}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Search Status" sx={{ background: "white", width: "100%" }} />
                    )}
                  />
                </TableCell>
              )}


                {columnFilter?.includes("Action") && <TableCell></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData?.length > 0 &&
                tableData.slice(0, rowsPerPage).map((row, index) => {
                  // Check if request is unread (opened is false or undefined)
                  const isUnread = viewType === 'inbox' && !row.opened;
                  const fontWeight = isUnread ? 'bold' : 'normal';
                  
                  return (
                    <TableRow key={index} sx={{ fontWeight }}>                  
                      <TableCell component="th" scope="row" align="center" sx={{ fontSize: tableFontSize, fontWeight }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                        {row?.uniqueReqId}
                      </TableCell>
                      {columnFilter?.includes("Req_Type") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {row?.req_type}
                        </TableCell>
                      )}
                      {columnFilter?.includes("Req_Creator") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {row?.req_creator_details}
                        </TableCell>
                      )}
                      {columnFilter?.includes("Store") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {row?.req_store}
                        </TableCell>
                      )}

                      {columnFilter?.includes("Created_At") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {moment(row?.created_at)?.format("DD-MM-YYYY | HH:mm:ss")}
                        </TableCell>
                      )}
                      {columnFilter?.includes("Currently_With") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {row?.curr_sent_to_string}
                        </TableCell>
                      )}
                      {columnFilter?.includes("Status") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {row?.curr_status}
                        </TableCell>
                      )}
                      {columnFilter?.includes("Action") && (
                        <TableCell sx={{ ...styles.cell, width: '10%', textAlign: 'center', fontWeight }}>
                          <StyledButton
                            sx={styles.actionButton}
                            onClick={(e) => handleEditclick(e, row, row.req_type)}
                          >
                            View/Action
                          </StyledButton>
                        </TableCell>
                      )}
                      {columnFilter?.includes("Warranty Date") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          {moment(row?.warrantyexp_date).format("DD-MM-YYYY")}
                        </TableCell>
                      )}
                      {columnFilter?.includes("Detail") && (
                        <TableCell align="left" sx={{ fontSize: tableFontSize, fontWeight }}>
                          <DetailsIcon
                            sx={{ color: "green", fontSize: "40px", "&:hover": { cursor: "pointer" } }}
                            onClick={(e) => handleDetailsClick(e, row)}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 100, 500, 1000]}
                  colSpan={3}
                  count={filteredData?.totalFilteredRequests || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { "aria-label": "rows per page" },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>

      {/* {assetdetailmodalopen && (
        <MyAssetDetailModal
          assetdetailmodalopen={assetdetailmodalopen}
          assetdetailModaldata={assetdetailModaldata}
          handleAssetDetailModalClose={handleAssetDetailModalClose}
          users={users}
        />
      )} */}
    </>
  );
}